/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/member-ordering */
import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FetchData } from "app/shared/generalMethods";
import { MatSnackBar } from "@angular/material/snack-bar";
import { AttendanceService } from "app/services/attendance.service";
import { Subscription, catchError, forkJoin, of } from "rxjs";
import { NgForm } from "@angular/forms";

interface Registration {
  Id: string;
  Kids__c: string;
  Kid_name__c?: string;
  Staff_Profile__c?: string;
  Class_Schedule__c: string;
  Total_Due__c: number;
  attendanceId?: string;
  attendanceStatus?: string | null;
  paymentStatus: string;
}

@Component({
  selector: "app-attendance-detail",
  templateUrl: "./attendance-detail.component.html",
  styleUrls: ["./attendance-detail.component.scss"],
})
export class AttendanceDetailComponent implements OnInit, OnDestroy {
  @ViewChild("attendanceForm") attendanceForm: NgForm;
  classScheduleId: string | null = null;
  classSchedule: any = null;
  registrations: Registration[] = [];
  existingAttendances: any[] = [];
  isLoading = false;
  isSaving = false;
  pageLoader = false;
  pageLoaderText = "Loading attendance data...";
  hasExistingAttendance = false;
  allClassSchedules: any[] = [];
  isEditing = false;
  originalAttendanceStatus: { [key: string]: string } = {};
  canViewAttendance = false;
  canEditAttendance = false;
  canCreateAttendance = false;
  canEditOrCreateAttendance = false;
  isAdmin: boolean = false;
  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private _fetchData: FetchData,
    private _snackBar: MatSnackBar,
    private attendanceService: AttendanceService
  ) {}

  ngOnInit(): void {
    this.checkPermissions();
    if (this.canViewAttendance) {
      this.classScheduleId =
        this.route.snapshot.paramMap.get("classScheduleId");
      this.loadAllClassSchedules();
      this.checkLocalStorageForAttendance();
      this.loadAttendanceData();
    } else {
      this.pageLoader = false;
    }
  }

  private parsePermissions(raw: string): any | null {
    try {
      const decoded = raw.replace(/&quot;/g, '"');
      return JSON.parse(decoded);
    } catch (err) {
      console.error("Failed to parse permissions JSON:", err);
      return null;
    }
  }

  private checkPermissions(): void {
    const { countAdmin, countStaff, lstStaffData } = this._fetchData.data;

    if (countAdmin === 1) {
      this.isAdmin = true;
      this.canViewAttendance = true;
      this.canEditAttendance = true;
      this.canCreateAttendance = true;
      this.canEditOrCreateAttendance = true;
      return;
    }

    if (countStaff === 1 && lstStaffData?.length > 0) {
      const permissionSetsRaw = lstStaffData[0].Permission_Sets__c;
      const permissions = this.parsePermissions(permissionSetsRaw);

      if (permissions?.attendance) {
        this.canViewAttendance = permissions.attendance.view ?? false;
        this.canEditAttendance = permissions.attendance.edit ?? false;
        this.canCreateAttendance = permissions.attendance.create ?? false;
        this.canEditOrCreateAttendance =
          this.canEditAttendance || this.canCreateAttendance;
      } else {
        console.warn("No attendance permissions defined in Permission_Sets__c");
      }
    } else {
      this.canViewAttendance = false;
      this.canEditAttendance = false;
      this.canCreateAttendance = false;
      this.canEditOrCreateAttendance = false;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private loadAllClassSchedules(): void {
    const dataSet = this._fetchData.data;
    const adminId = localStorage.getItem("adminId");

    if (dataSet && dataSet.lstClassSchedules && adminId) {
      const adminPrograms = (dataSet.lstPrograms || []).filter(
        (program: any) => program.Administrator__c === adminId
      );
      const adminProgramIds = adminPrograms.map((p: any) => p.Id);

      this.allClassSchedules = [...dataSet.lstClassSchedules]
        .filter((schedule: any) =>
          adminProgramIds.includes(schedule.Program__c)
        )
        .sort((a, b) => {
          const dateA = new Date(a.Date__c).getTime();
          const dateB = new Date(b.Date__c).getTime();
          const now = new Date().getTime();

          if ((dateA < now && dateB < now) || (dateA >= now && dateB >= now)) {
            return dateB - dateA;
          }
          return dateA < now ? -1 : 1;
        });
    }
  }

  private checkLocalStorageForAttendance(): void {
    if (this.classScheduleId) {
      const savedAttendance = localStorage.getItem(
        `attendance_${this.classScheduleId}`
      );
      if (savedAttendance) {
        const parsedAttendance = JSON.parse(savedAttendance);
        if (parsedAttendance.message) {
          this.showSuccess(parsedAttendance.message);
        }
      }
    }
  }

  private loadAttendanceData(): void {
    this.pageLoader = true;
    if (!this.classScheduleId) {
      this.showError("Invalid class schedule");
      this.pageLoader = false;
      return;
    }

    const dataSet = this._fetchData.data;
    if (!dataSet) {
      this.showError("No data available");
      this.pageLoader = false;
      return;
    }

    try {
      this.classSchedule = (dataSet.lstClassSchedules || []).find(
        (cs: any) => cs.Id === this.classScheduleId
      );

      if (!this.classSchedule) {
        this.showError("Class schedule not found");
        this.pageLoader = false;
        return;
      }

      this.existingAttendances = (dataSet.lstAttendance || []).filter(
        (att: any) => att.Class_Schedule__c === this.classScheduleId
      );

      this.hasExistingAttendance = this.existingAttendances.length > 0;

      this.processRegistrations(dataSet.lstRegistrationSchedules || []);
      this.pageLoader = false;
    } catch (error) {
      this.showError("Error loading attendance data");
      this.pageLoader = false;
      console.error("❌ Error loading attendance data:", error);
    }
  }

  private processRegistrations(allRegistrations: any[]): void {
    const filteredRegistrations = allRegistrations
      .filter((rs: any) => rs.Class_Schedule__c === this.classScheduleId)
      .filter((rs: any) => rs.Kids__r?.Is_Deleted__c === false);

    this.registrations = filteredRegistrations.map((reg) => {
      const existingAttendance = this.existingAttendances.find(
        (att: any) => att.Kids__c === reg.Kids__c
      );

      const registrationData = {
        ...reg,
        attendanceId: existingAttendance?.Id,
        attendanceStatus: existingAttendance
          ? existingAttendance.Is_Absent__c
            ? "Absent"
            : "Present"
          : null,
        paymentStatus: reg.Total_Due__c === 0 ? "Paid" : "Unpaid",
        Kid_name__c: reg.Kid_name__c || this.getKidName(reg.Kids__c),
      };

      return registrationData;
    });
  }

  private getKidName(kidId: string): string {
    const dataSet = this._fetchData.data;
    if (dataSet && dataSet.lstKids) {
      const kid = dataSet.lstKids.find((k: any) => k.Id === kidId);
      return kid ? kid.Name : "Unknown";
    }
    return "Unknown";
  }

  setAttendance(registration: Registration, status: string): void {
    if (
      (!this.hasExistingAttendance || this.isEditing) &&
      this.canEditAttendance
    ) {
      registration.attendanceStatus = status;
    } else {
    }
  }

  onFormSubmit(): void {
    if (!this.classScheduleId || !this.classSchedule) {
      this.showError("Class Schedule data missing");
      return;
    }

    // Get authentication info
    const localAdminId = localStorage.getItem("adminId");
    const staffData = this._fetchData.data.lstStaffData?.[0];
    const isStaff = !localAdminId && staffData?.Id;
    const staffId = isStaff ? staffData.Id : null;
    const adminId = isStaff ? null : localAdminId;

    if (!adminId && !staffId) {
      this.showError("Neither Admin ID nor Staff ID found");
      return;
    }

    const anyMarked = this.registrations.some(
      (reg) =>
        reg.attendanceStatus === "Present" || reg.attendanceStatus === "Absent"
    );

    if (!anyMarked) {
      this.showError(
        "Please mark attendance for at least one student before saving"
      );
      return;
    }

    const hasUnmarkedStudents = this.registrations.some(
      (reg) => !reg.attendanceStatus
    );

    if (hasUnmarkedStudents) {
      this.registrations.forEach((reg) => {
        if (!reg.attendanceStatus) {
          reg.attendanceStatus = "Absent";
        }
      });
    }

    // Analyze records for save strategy
    const hasExistingRecords = this.registrations.some(
      (reg) => reg.attendanceId
    );
    const hasNewRecords = this.registrations.some(
      (reg) => !reg.attendanceId && reg.attendanceStatus
    );

    this.isSaving = true;
    this.pageLoader = true;
    this.pageLoaderText = "Saving attendance records...";

    // Handle different scenarios
    if (this.isEditing && hasExistingRecords && hasNewRecords) {
      this.handleMixedRecordsSave(isStaff, adminId, staffId);
    } else if (this.isEditing && hasExistingRecords) {
      this.saveEditedAttendance();
    } else {
      this.saveNewAttendance(isStaff, adminId, staffId);
    }
  }

  private handleMixedRecordsSave(
    isStaff: boolean,
    adminId: string | null,
    staffId: string | null
  ): void {
    const existingRecordsToUpdate = this.registrations
      .filter((reg) => reg.attendanceId && reg.attendanceStatus && reg.Kids__c)
      .map((reg) => ({
        attendanceId: reg.attendanceId!,
        isAbsent: reg.attendanceStatus === "Absent",
        kidId: reg.Kids__c,
        kidName: reg.Kid_name__c,
      }));

    const newRecordsToCreate = this.registrations
      .filter((reg) => {
        const alreadyExists = this.existingAttendances.some(
          (att) =>
            att.Kids__c === reg.Kids__c &&
            att.Class_Schedule__c === this.classScheduleId
        );
        const shouldCreate =
          !alreadyExists &&
          !reg.attendanceId &&
          reg.attendanceStatus &&
          reg.Kids__c;

        if (shouldCreate) {
        }

        return shouldCreate;
      })
      .map((reg) => ({
        staff: reg.Staff_Profile__c || null,
        classSchedule: this.classScheduleId!,
        kidId: reg.Kids__c,
        inTime: this.classSchedule.Start_Time__c,
        outTime: this.classSchedule.End_Time__c,
        isAbsent: reg.attendanceStatus === "Absent",
        kidName: reg.Kid_name__c,
      }));

    if (
      existingRecordsToUpdate.length === 0 &&
      newRecordsToCreate.length === 0
    ) {
      this.showError("No valid changes to save");
      this.resetLoadingStates();
      return;
    }

    const updateObservable =
      existingRecordsToUpdate.length > 0
        ? isStaff
          ? this.attendanceService.updateAttendanceByStaff(
              existingRecordsToUpdate,
              staffId!
            )
          : this.attendanceService.updateAttendance(
              existingRecordsToUpdate,
              adminId!
            )
        : of(null);

    const createObservable =
      newRecordsToCreate.length > 0
        ? isStaff
          ? this.attendanceService.addAttendanceByStaff(
              newRecordsToCreate,
              staffId!
            )
          : this.attendanceService.addAttendance(newRecordsToCreate, adminId!)
        : of(null);

    this.subscriptions.add(
      forkJoin([updateObservable, createObservable])
        .pipe(
          catchError((error) => {
            console.error("❌ Error in mixed records save:", error);
            this.showError(
              error.message || "Failed to save attendance records"
            );
            this.resetLoadingStates();
            return of(null);
          })
        )
        .subscribe({
          next: (responses) => {
            if (responses) {
              const responseMsg = responses[0]?.msg || responses[1]?.msg;
              if (responseMsg) {
                this._fetchData.setLocalStorage(responseMsg);
              }

              this.hasExistingAttendance = true;
              this.isEditing = false;
              this.showSuccess("Attendance records saved successfully");
              this.loadAttendanceData();
            }
          },
          error: (error) => {
            console.error("❌ Mixed records save failed:", error);
            this.showError(
              error.message || "Failed to save attendance records"
            );
            this.resetLoadingStates();
          },
          complete: () => {
            this.resetLoadingStates();
          },
        })
    );
  }

  private saveNewAttendance(
    isStaff: boolean,
    adminId: string | null,
    staffId: string | null
  ): void {
    const registrationsToSave = this.registrations
      .filter((reg) => {
        const alreadyExists = this.existingAttendances.some(
          (att) =>
            att.Kids__c === reg.Kids__c &&
            att.Class_Schedule__c === this.classScheduleId
        );
        const shouldSave =
          !alreadyExists &&
          !reg.attendanceId &&
          reg.attendanceStatus &&
          reg.Kids__c;

        if (shouldSave) {
        } else {
        }

        return shouldSave;
      })
      .map((reg) => ({
        staff: reg.Staff_Profile__c || null,
        classSchedule: this.classScheduleId!,
        kidId: reg.Kids__c,
        inTime: this.classSchedule.Start_Time__c,
        outTime: this.classSchedule.End_Time__c,
        isAbsent: reg.attendanceStatus === "Absent",
        kidName: reg.Kid_name__c,
      }));

    if (registrationsToSave.length === 0) {
      this.showError("No valid attendance records to save");
      this.resetLoadingStates();
      return;
    }

    if (isStaff) {
      this.subscriptions.add(
        this.attendanceService
          .addAttendanceByStaff(registrationsToSave, staffId!)
          .subscribe({
            next: (response) => {
              this._fetchData.setLocalStorage(response.msg);
              this.hasExistingAttendance = true;
              this.showSuccess("Attendance saved successfully");
              this.loadAttendanceData();
            },
            error: (error) => {
              console.error("❌ Staff save failed:", error);
              this.showError(error.message || "Failed to save attendance");
              this.resetLoadingStates();
            },
            complete: () => {
              this.resetLoadingStates();
            },
          })
      );
    } else {
      this.subscriptions.add(
        this.attendanceService
          .addAttendance(registrationsToSave, adminId!)
          .subscribe({
            next: (response) => {
              this._fetchData.setLocalStorage(response.msg);
              this.hasExistingAttendance = true;
              this.showSuccess("Attendance saved successfully");
              this.loadAttendanceData();
            },
            error: (error) => {
              console.error("❌ Admin save failed:", error);
              this.showError(error.message || "Failed to save attendance");
              this.resetLoadingStates();
            },
            complete: () => {
              this.resetLoadingStates();
            },
          })
      );
    }
  }

  toggleEditMode(): void {
    if (!this.canEditAttendance) {
      this.showError("You do not have permission to edit attendance");
      return;
    }

    this.isEditing = !this.isEditing;

    if (this.isEditing) {
      this.originalAttendanceStatus = {};
      this.registrations.forEach((reg) => {
        if (reg.Kids__c) {
          this.originalAttendanceStatus[reg.Kids__c] =
            reg.attendanceStatus || "";
        }
      });
    } else {
      this.registrations.forEach((reg) => {
        if (
          reg.Kids__c &&
          this.originalAttendanceStatus[reg.Kids__c] !== undefined
        ) {
          reg.attendanceStatus = this.originalAttendanceStatus[reg.Kids__c];
        }
      });
    }
  }

  saveEditedAttendance(): void {
    if (!this.classScheduleId || !this.classSchedule) {
      this.showError("Class Schedule data missing");
      return;
    }

    // Get authentication info
    const localAdminId = localStorage.getItem("adminId");
    const staffData = this._fetchData.data.lstStaffData?.[0];
    const isStaff = !localAdminId && staffData?.Id;
    const staffId = isStaff ? staffData.Id : null;
    const adminId = isStaff ? null : localAdminId;

    if (!adminId && !staffId) {
      this.showError("Neither Admin ID nor Staff ID found");
      return;
    }

    // Check if there are any changes
    const hasChanges = this.registrations.some((reg) => {
      const originalStatus = this.originalAttendanceStatus[reg.Kids__c];
      const hasChange = reg.attendanceStatus !== originalStatus;

      if (hasChange) {
      }

      return hasChange;
    });

    if (!hasChanges) {
      this.isEditing = false;
      this.showSuccess("No changes to save");
      return;
    }

    this.isSaving = true;
    this.pageLoader = true;
    this.pageLoaderText = "Updating attendance records...";

    // Filter records that have attendanceId AND have a status (not empty)
    const updates = this.registrations
      .filter((reg) => reg.attendanceId && reg.attendanceStatus && reg.Kids__c)
      .map((reg) => ({
        attendanceId: reg.attendanceId,
        isAbsent: reg.attendanceStatus === "Absent",
        kidId: reg.Kids__c,
        kidName: reg.Kid_name__c,
      }));

    if (updates.length === 0) {
      const newRecords = this.registrations.filter(
        (reg) => !reg.attendanceId && reg.attendanceStatus && reg.Kids__c
      );

      if (newRecords.length > 0) {
        this.saveNewAttendance(isStaff, adminId, staffId);
        return;
      }

      this.showError("No valid changes to save");
      this.resetLoadingStates();
      return;
    }

    if (isStaff) {
      this.subscriptions.add(
        this.attendanceService
          .updateAttendanceByStaff(updates, staffId!)
          .subscribe({
            next: (response) => {
              this._fetchData.setLocalStorage(response.msg);
              this.isEditing = false;

              // Check if there are new records that also need to be saved
              const newRecords = this.registrations.filter(
                (reg) =>
                  !reg.attendanceId && reg.attendanceStatus && reg.Kids__c
              );

              if (newRecords.length > 0) {
                this.saveNewAttendance(isStaff, adminId, staffId);
              } else {
                this.showSuccess("Attendance updated successfully");
                this.loadAttendanceData();
                this.resetLoadingStates();
              }
            },
            error: (error) => {
              console.error("❌ Staff update failed:", error);
              this.showError(error.message || "Failed to update attendance");
              this.resetLoadingStates();
            },
          })
      );
    } else {
      this.subscriptions.add(
        this.attendanceService.updateAttendance(updates, adminId!).subscribe({
          next: (response) => {
            this._fetchData.setLocalStorage(response.msg);
            this.isEditing = false;

            // Check if there are new records that also need to be saved
            const newRecords = this.registrations.filter(
              (reg) => !reg.attendanceId && reg.attendanceStatus && reg.Kids__c
            );

            if (newRecords.length > 0) {
              this.saveNewAttendance(isStaff, adminId, staffId);
            } else {
              this.showSuccess("Attendance updated successfully");
              this.loadAttendanceData();
              this.resetLoadingStates();
            }
          },
          error: (error) => {
            console.error("❌ Admin update failed:", error);
            this.showError(error.message || "Failed to update attendance");
            this.resetLoadingStates();
          },
        })
      );
    }
  }

  private resetLoadingStates(): void {
    this.isSaving = false;
    this.pageLoader = false;
  }

  private showError(message: string): void {
    console.error("❌ Error:", message);
    this._snackBar.open(message, "Close", {
      duration: 5000,
      panelClass: ["error-snackbar"],
    });
  }

  private showSuccess(message: string): void {
    this._snackBar.open(message, "Close", {
      duration: 3000,
      panelClass: ["success-snackbar"],
    });
  }

  // Keep your existing formatTime, formatDate, isPastClass methods...
  formatTime(timeString: string): string {
    if (!timeString) return "--";
    try {
      const timePart = timeString.includes("T")
        ? timeString.split("T")[1]
        : timeString;
      const [hours, minutes] = timePart.split(":");
      const hourNum = parseInt(hours, 10);
      const period = hourNum >= 12 ? "PM" : "AM";
      const hour12 = hourNum % 12 || 12;
      return `${hour12}:${minutes.substring(0, 2)} ${period}`;
    } catch (e) {
      return timeString;
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return "--";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  }

  isPastClass(dateString: string): boolean {
    if (!dateString) return false;
    try {
      const classDate = new Date(dateString);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return classDate < today;
    } catch (e) {
      return false;
    }
  }
}
